import Link from 'next/link';
import { ChevronRight, File } from 'lucide-react';
import type { ReactNode } from 'react';
import { Typography } from '../ui';
import { useBasePath } from '../../../lib/use-base-path';
import { useGenericBi } from '../../../lib/use-generic-bi';
import styles from './DestinationCard.module.css';

// "What they know about you" at the foot of Your Agents (Figma `10261:85948`): the glyph on the
// title's line, the blurb below starting at the card's left edge (under the icon, not indented
// beside it — same shape as the top Tasks/Files tiles), and a 16px drill-in chevron in the primary
// ink, centred against the whole card at the far end (`10261:86167`). The whole card opens `href`.

const PAGE = 'your_agents';

export function DestinationCard({
  title,
  subtitle,
  icon,
  href,
  button,
}: {
  title: string;
  subtitle: string;
  icon: ReactNode;
  href: string;
  button: string;
}) {
  const basePath = useBasePath();
  const { logPageClickBi } = useGenericBi();
  return (
    <Link
      className={styles.card}
      href={href}
      as={`${basePath}${href}`}
      onClick={() => logPageClickBi({ page_name: PAGE, button_name: button })}
    >
      <span className={styles.body}>
        <span className={styles.head}>
          <span className={styles.glyph} aria-hidden>
            {icon}
          </span>
          <Typography variant="h4" color="strong" component="span">
            {title}
          </Typography>
        </span>
        <Typography variant="p2" color="secondary" component="span">
          {subtitle}
        </Typography>
      </span>
      <span className={`${styles.chevron} dir-flip`} aria-hidden>
        <ChevronRight size={16} />
      </span>
    </Link>
  );
}

// The design's activity glyph: a clock inside a document.
export function ActivityLogIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden>
      <path d="M13.6951 18.3342H15.3617C15.8038 18.3342 16.2277 18.1586 16.5402 17.846C16.8528 17.5334 17.0284 17.1095 17.0284 16.6675V6.66749C17.029 6.40341 16.9774 6.14182 16.8764 5.89781C16.7754 5.65381 16.6271 5.43221 16.4401 5.24582L13.4501 2.25582C13.2637 2.06875 13.0421 1.92045 12.7981 1.81947C12.5541 1.71849 12.2925 1.66684 12.0284 1.66749H5.36173C4.91971 1.66749 4.49578 1.84308 4.18322 2.15564C3.87066 2.4682 3.69507 2.89213 3.69507 3.33415V5.70915" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12.0276 1.66748V5.83415C12.0276 6.05516 12.1154 6.26712 12.2717 6.4234C12.4279 6.57968 12.6399 6.66748 12.8609 6.66748H17.0276" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M7.02759 11.6675V13.5008L8.36092 14.3341" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M7.02759 18.3325C9.78901 18.3325 12.0276 16.0939 12.0276 13.3325C12.0276 10.5711 9.78901 8.33252 7.02759 8.33252C4.26616 8.33252 2.02759 10.5711 2.02759 13.3325C2.02759 16.0939 4.26616 18.3325 7.02759 18.3325Z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// The cross-workspace Files destination's glyph — the design's document (not folder) mark
// (Figma `11610:46930`), drawn from lucide per CLAUDE.md → Icons unlike its two
// Figma-exported siblings above.
export function FilesIcon() {
  return <File size={16} aria-hidden />;
}

// The design's knowledge glyph: the open book with ruled pages.
export function KnowledgeIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden>
      <path d="M10.3613 5.83252V17.4992" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M13.6943 10H15.361" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M13.6943 6.66748H15.361" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M2.86165 15C2.64064 15 2.42868 14.9122 2.2724 14.7559C2.11612 14.5996 2.02832 14.3877 2.02832 14.1667V3.33333C2.02832 3.11232 2.11612 2.90036 2.2724 2.74408C2.42868 2.5878 2.64064 2.5 2.86165 2.5H7.02832C7.91238 2.5 8.76022 2.85119 9.38534 3.47631C10.0105 4.10143 10.3617 4.94928 10.3617 5.83333C10.3617 4.94928 10.7128 4.10143 11.338 3.47631C11.9631 2.85119 12.8109 2.5 13.695 2.5H17.8617C18.0827 2.5 18.2946 2.5878 18.4509 2.74408C18.6072 2.90036 18.695 3.11232 18.695 3.33333V14.1667C18.695 14.3877 18.6072 14.5996 18.4509 14.7559C18.2946 14.9122 18.0827 15 17.8617 15H12.8617C12.1986 15 11.5627 15.2634 11.0939 15.7322C10.625 16.2011 10.3617 16.837 10.3617 17.5C10.3617 16.837 10.0983 16.2011 9.62942 15.7322C9.16058 15.2634 8.52469 15 7.86165 15H2.86165Z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5.36133 10H7.02799" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5.36133 6.66748H7.02799" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
